import React from "react";

// Phase 3D.4-C - Presentational card for the SINGLE strictly SIMULATED campaign
// produced by buildSimulatedCampaign(recommendationResult).
//
// PURE PRESENTATION ONLY:
//   * Renders EXCLUSIVELY the fields returned by the 3D.4-B engine.
//   * NO campaign recomputation, NO API/network call, NO env read, NO storage.
//   * NO hardcoded market/figure/period/channel/budget.
//   * NO default scenario, NO fallback fabrication.
//   * Fail-closed guards: renders NOTHING unless the object is an explicit,
//     disabled, simulated campaign.
//   * NO launch/publish/activate/connect control, NO editable form, NO ad-network
//     brand, NO result KPI, NO performance gauge, NO projection chart.
//   * Never mutates its input.

import ProvenanceBadges from "@/components/provenance/ProvenanceBadges";
import { MISSING, periodLabel } from "@/lib/savoieKpis";

// --- Visual-only formatters (fr-FR). Never used for any decision. ---------
const nf = new Intl.NumberFormat("fr-FR");
const fmtInt = (v) => (typeof v === "number" ? nf.format(Math.round(v)) : MISSING);
const fmtPct1 = (v) =>
  typeof v === "number" ? `${(Math.round(v * 10) / 10).toFixed(1).replace(".", ",")} %` : MISSING;
const fmtPctSigned = (v) =>
  typeof v === "number"
    ? `${v >= 0 ? "+" : ""}${(Math.round(v * 10) / 10).toFixed(1).replace(".", ",")} %`
    : MISSING;

// "2025-12" -> "décembre 2025" (reuses the pure label helper; no recompute).
function frPeriod(iso) {
  if (typeof iso !== "string") return MISSING;
  const [y, m] = iso.split("-").map((x) => Number(x));
  if (!Number.isFinite(y) || !Number.isFinite(m)) return MISSING;
  return periodLabel({ year: y, month: m });
}

// Small inline "hypothesis" tag (not colour-only: carries explicit text).
function AssumptionTag() {
  return (
    <span
      className="inline-flex items-center rounded border border-violet-300 bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700"
      data-testid="assumption-tag"
    >
      Hypothèse
    </span>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-t border-slate-100 py-1.5 first:border-t-0">
      <dt className="text-sm text-slate-600">{label}</dt>
      <dd className="text-sm font-semibold text-slate-900" data-testid={testid}>{value}</dd>
    </div>
  );
}

const ASSUMPTION_KIND_LABELS = {
  budget: "Budget",
  channels: "Canaux",
  objective: "Objectif",
  period: "Période",
};

// Verbatim note texts kept as JS strings (avoid JSX unescaped-entity issues).
const PERIOD_NOTE =
  "Hypothèse de période pour la démonstration — aucune diffusion n'est programmée.";
const OBJECTIVE_NOTE = "Hypothèse synthétique — non issue d'une plateforme publicitaire.";
const BUDGET_NOTE =
  "Budget fictif de démonstration — il ne constitue pas un conseil budgétaire et ne " +
  "provient d'aucune donnée de dépense touristique.";
const NO_RESULTS_NOTE =
  "Cette simulation ne fournit aucune estimation de performance, de ROI, d'impressions, " +
  "de clics, de visiteurs ou de conversions.";
const ASSUMPTIONS_NOTE =
  "Ces hypothèses ne sont ni des observations réelles ni des prédictions.";

export default function CampaignSimulationCard({ campaignResult }) {
  // Fail-closed: only ever render an explicit, disabled, simulated campaign.
  const c = campaignResult;
  if (!c || c.status !== "simulated") return null;
  if (c.simulation !== true) return null;
  if (c.activation_status !== "disabled") return null;
  if (c.publish_status !== "disabled") return null;

  const aud = c.audience || {};
  const period = c.period || {};
  const budget = c.budget || {};
  const ev = c.evidence_reference || {};
  const channels = Array.isArray(c.channels) ? c.channels : [];
  const objectives = Array.isArray(c.objectives) ? c.objectives : [];
  const assumptions = Array.isArray(c.assumptions) ? c.assumptions : [];

  return (
    <section
      className="rounded-2xl border border-violet-200 bg-violet-50/60 p-5 shadow-sm"
      aria-labelledby="campaign-sim-title"
      data-testid="campaign-simulation-card"
    >
      {/* Title + explicit badges (Simulation + Données de démonstration). */}
      <div className="flex flex-wrap items-center gap-2">
        <h2
          id="campaign-sim-title"
          className="font-heading text-lg font-bold tracking-tight text-violet-900 sm:text-xl"
          data-testid="campaign-sim-title"
        >
          Campagne simulée
        </h2>
        <ProvenanceBadges meta={c.meta} />
      </div>

      {/* 1. Scenario name */}
      <p className="mt-3 text-sm font-semibold text-slate-900" data-testid="campaign-sim-name">
        {c.name}
      </p>

      {/* 2. Simulated audience (from campaignResult.audience only). */}
      <p className="mt-1 text-sm text-slate-700" data-testid="campaign-sim-audience">
        Audience simulée : <span className="font-semibold">{aud.source_market_name}</span>
        {" "}({aud.source_market_code})
      </p>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* 3. Simulated period */}
        <div className="rounded-xl border border-violet-200 bg-white p-4" data-testid="campaign-sim-period-block">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-violet-700">
            Période simulée
          </h3>
          <p className="text-sm font-semibold text-slate-900" data-testid="campaign-sim-period">
            {frPeriod(period.month)}
          </p>
          <p className="mt-1 text-xs italic text-slate-500" data-testid="campaign-sim-period-note">
            {PERIOD_NOTE}
          </p>
        </div>

        {/* 4. Simulated objective(s) */}
        <div className="rounded-xl border border-violet-200 bg-white p-4" data-testid="campaign-sim-objective-block">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-violet-700">
            Objectif simulé
          </h3>
          <ul className="space-y-1" data-testid="campaign-sim-objective">
            {objectives.map((o) => (
              <li key={o.code} className="flex items-center gap-2 text-sm text-slate-800">
                <AssumptionTag />
                <span>{o.label}</span>
              </li>
            ))}
          </ul>
          <p className="mt-1 text-xs italic text-slate-500" data-testid="campaign-sim-objective-note">
            {OBJECTIVE_NOTE}
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* 5. Simulated channels */}
        <div className="rounded-xl border border-violet-200 bg-white p-4" data-testid="campaign-sim-channels-block">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-violet-700">
            Canaux simulés
          </h3>
          <ul className="space-y-1.5" data-testid="campaign-sim-channels">
            {channels.map((ch) => (
              <li
                key={ch.code}
                className="flex items-center gap-2 text-sm text-slate-800"
                data-testid={`campaign-sim-channel-${ch.code}`}
              >
                <AssumptionTag />
                <span>{ch.label}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* 6. Synthetic budget */}
        <div className="rounded-xl border border-violet-200 bg-white p-4" data-testid="campaign-sim-budget-block">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-violet-700">
            Budget synthétique
          </h3>
          <p className="flex items-center gap-2 text-sm text-slate-900">
            <AssumptionTag />
            <span className="text-lg font-bold" data-testid="campaign-sim-budget">
              {fmtInt(budget.amount)} {budget.currency}
            </span>
          </p>
          <p className="mt-1 text-xs italic text-slate-500" data-testid="campaign-sim-budget-note">
            {BUDGET_NOTE}
          </p>
        </div>
      </div>

      {/* 7. Expected results (explicitly none). */}
      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="campaign-sim-expected-block">
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Résultats attendus
        </h3>
        <p className="text-sm font-semibold text-slate-900" data-testid="campaign-sim-expected">
          Aucun résultat prédit
        </p>
        <p className="mt-1 text-xs italic text-slate-500" data-testid="campaign-sim-no-results-note">
          {NO_RESULTS_NOTE}
        </p>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* 8. Reference evidence (led to the recommendation; NOT results). */}
        <div className="rounded-xl border border-indigo-200 bg-white p-4" data-testid="campaign-sim-evidence">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-indigo-700">
            Preuves ayant conduit à la recommandation
          </h3>
          <dl>
            <Row label="Période observée" value={frPeriod(ev.evidence_period)} testid="campaign-sim-ev-period" />
            <Row label="Période de comparaison" value={frPeriod(ev.comparison_period)} testid="campaign-sim-ev-comparison" />
            <Row label="Croissance des arrivées" value={fmtPctSigned(ev.growth_percent)} testid="campaign-sim-ev-growth" />
            <Row label="Arrivées visiteurs" value={fmtInt(ev.visitor_arrivals)} testid="campaign-sim-ev-arrivals" />
            <Row label="Part du marché" value={fmtPct1(ev.market_share_percent)} testid="campaign-sim-ev-share" />
          </dl>
        </div>

        {/* 9. Scenario assumptions (fictional, non-predictive). */}
        <div className="rounded-xl border border-violet-200 bg-white p-4" data-testid="campaign-sim-assumptions">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-violet-700">
            Hypothèses fictives du scénario
          </h3>
          <ul className="space-y-1.5">
            {assumptions.map((a) => (
              <li
                key={a.kind}
                className="flex items-start gap-2 text-sm text-slate-800"
                data-testid={`campaign-sim-assumption-${a.kind}`}
              >
                <AssumptionTag />
                <span>
                  <span className="font-semibold">{ASSUMPTION_KIND_LABELS[a.kind] || a.kind} : </span>
                  {a.detail}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs italic text-slate-500" data-testid="campaign-sim-assumptions-note">
            {ASSUMPTIONS_NOTE}
          </p>
        </div>
      </div>

      {/* 10. Safety status (from the campaign flags; no controls). */}
      <div className="mt-4 flex flex-wrap gap-2" data-testid="campaign-sim-security">
        <span
          className="inline-flex items-center rounded-full border border-slate-300 bg-white px-2.5 py-0.5 text-xs font-semibold text-slate-700"
          data-testid="campaign-sim-activation"
        >
          Activation désactivée
        </span>
        <span
          className="inline-flex items-center rounded-full border border-slate-300 bg-white px-2.5 py-0.5 text-xs font-semibold text-slate-700"
          data-testid="campaign-sim-publish"
        >
          Publication désactivée
        </span>
        <span
          className="inline-flex items-center rounded-full border border-slate-300 bg-white px-2.5 py-0.5 text-xs font-semibold text-slate-700"
          data-testid="campaign-sim-external"
        >
          Aucune campagne externe créée
        </span>
      </div>

      {/* 11. Exact disclaimer. */}
      <p className="mt-4 text-xs text-slate-500" data-testid="campaign-sim-disclaimer">
        {c.disclaimer}
      </p>
    </section>
  );
}
